import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { useLanguage } from '../hooks/useLanguage';
import { getCuriosityData, CuriosityData } from '../data/curiositiesData';
import { wildlifeItems } from '../data/wildlifeData';
import { gastronomyItems } from '../data/gastronomyData';
import Header from '../components/landing/Header';
import Footer from '../components/landing/Footer';
import { usePageTitle } from '../hooks/usePageTitle';

export const CuriosityDetailsPage: React.FC = () => {
  const { curiosityId } = useParams<{ curiosityId: string }>();
  const { t } = useLanguage();

  const curiosityData = curiosityId ? getCuriosityData(curiosityId) : null;

  // Set page title based on curiosity data
  // Prefer translations for title/subtitle if available
  const translatedName = curiosityId ? t(`curiosities.${curiosityId}.name`) : undefined;
  const translatedSubtitleKey = curiosityId ? `curiosities.${curiosityId}.subtitle` : undefined;
  const translatedSubtitle = translatedSubtitleKey ? t(translatedSubtitleKey) : undefined;
  usePageTitle(translatedName ? `${translatedName} - La Dolce Vita` : (curiosityData ? `${curiosityData.name} - La Dolce Vita` : undefined));


  // Helper function to get banner image path
  const getBannerImagePath = (curiosityId: string) => {
    return `/curiosities/${curiosityId}/${curiosityId}_banner.webp`;
  };

  // Helper function to get curiosity name for breadcrumb based on language
  const getCuriosityBreadcrumbName = (curiosityId: string) => {
    const curiosityNameMap: Record<string, Record<string, string>> = {
      'wildlife': {
        'en': 'Wildlife',
        'it': 'Wildlife',
        'nl': 'Wildlife'
      }
    };

    const currentLanguage = localStorage.getItem('language') || 'en';
    return curiosityNameMap[curiosityId]?.[currentLanguage] || curiosityData?.name || curiosityId;
  };

  if (!curiosityData) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <Header />
        <main className="flex-1 flex items-center justify-center py-16">
          <div className="text-center">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">Curiosità non trovata</h1>
            <p className="text-gray-600 mb-8">La curiosità che stai cercando non esiste.</p>
            <Link
              to="/"
              className="bg-blue-600 text-white font-bold py-3 px-6 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Torna alla Home
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const renderOverview = () => {
    // Special rendering for history: only show HTML description and a heading
    if (curiosityId === 'history') {
      return (
        <div className="space-y-6">
          <div className="bg-white rounded-lg p-6 shadow-sm">
            <div
              className="text-gray-700 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: t(`curiosities.${curiosityId}.description`) }}
            />
          </div>
        </div>
      );
    }

    const items = curiosityId === 'wildlife' ? wildlifeItems : gastronomyItems;
    
    return (
      <div className="space-y-6">
        {/* First part of description */}
        <div className="bg-white rounded-lg p-6 shadow-sm">
          <div className="text-gray-700 leading-relaxed">
            {t(`curiosities.${curiosityId}.description`)}
          </div>
        </div>

        {/* Items List */}
        <div className="space-y-8">
          {items.map((item, index) => (
            <div 
              key={item.id}
              className="bg-white rounded-lg p-6 shadow-sm"
            >
              <div className={`flex flex-col md:flex-row items-start gap-6 ${
                item.isReverse ? 'md:flex-row-reverse' : ''
              }`}>
                <div className="flex-shrink-0 w-full md:w-[28rem]">
                  <img
                    src={item.image}
                    alt={t(`curiosities.${curiosityId}.items.${item.id}.alt`)}
                    className="w-full md:w-[28rem] h-64 md:h-80 object-cover rounded-lg shadow-md"
                  />
                </div>
                <div className="flex-1 w-full">
                  <h2 className="text-2xl font-bold mb-4 text-gray-900">
                    {t(`curiosities.${curiosityId}.items.${item.id}.name`)}
                  </h2>
                  <div 
                    className="text-gray-700 leading-relaxed mb-4"
                    dangerouslySetInnerHTML={{ 
                      __html: t(`curiosities.${curiosityId}.items.${item.id}.description`) 
                    }}
                  />
                  {(() => {
                    if (curiosityId !== 'wildlife') return null;
                    const travelNoteKey = `curiosities.${curiosityId}.items.${item.id}.travelNote`;
                    const travelNoteValue = t(travelNoteKey);
                    // If translation function returns the key itself, it means translation not found
                    const travelNote = travelNoteValue === travelNoteKey ? '' : travelNoteValue;
                    return travelNote && travelNote.trim() && (
                      <div className="bg-gray-100 p-3 rounded-lg">
                        <p className="text-sm text-gray-600">
                          <strong>Note di viaggio:</strong> {travelNote}
                        </p>
                      </div>
                    );
                  })()}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Creative Commons Attribution */}
        <div className="bg-white rounded-lg p-6 shadow-sm">
          <p className="text-left text-xs text-gray-500">
            <span>{t('landing.footer.creativeCommons')}</span>
            <a 
              href={`/curiosities/${curiosityId}/wikimedia.info`}
              target="_blank" 
              rel="noopener noreferrer"
              className="text-blue-500 hover:text-blue-600 underline"
            >
              {t('landing.footer.fullCredits')}
            </a>
          </p>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      {/* Hero Section with Banner */}
      <div className="relative h-64 md:h-96">
        <img
          src={getBannerImagePath(curiosityId!)}
          alt={translatedName || curiosityData.name}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center">
          <div className="text-center text-white">
            <h1 className="text-4xl md:text-6xl font-bold mb-2">
              {translatedName || curiosityData.name}
            </h1>
            <p className="text-xl md:text-2xl">
              {(translatedSubtitle && translatedSubtitle !== translatedSubtitleKey) ? translatedSubtitle : curiosityData.subtitle}
            </p>
          </div>
        </div>
      </div>

      {/* Breadcrumbs */}
      <div className="bg-white border-b">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center space-x-2">
            <Link to="/" className="text-gray-500 hover:text-blue-600">{t('common.home')}</Link>
            <span className="text-gray-400">/</span>
            <Link 
              to="/#curiosities" 
              onClick={(e) => {
                e.preventDefault();
                // Naviga alla home e poi scrolla alla sezione curiosities
                window.location.href = '/#curiosities';
              }}
              className="text-gray-500 hover:text-blue-600 cursor-pointer"
            >
              Curiosità
            </Link>
            <span className="text-gray-400">/</span>
            <span className="bg-gradient-to-r from-blue-500 to-purple-600 text-white px-3 py-1 rounded-full text-sm font-medium">
              {getCuriosityBreadcrumbName(curiosityId!)}
            </span>
          </div>
        </div>
      </div>

      {/* Main Content - single section (no tabs) */}
      <div className="container mx-auto px-4 py-8">
        {renderOverview()}
      </div>


      <Footer />
    </div>
  );
};

export default CuriosityDetailsPage;
